import { CircleCheckBig, CircleX } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';
import { IconProps, toast, ToastOptions, TypeOptions } from 'react-toastify';

interface ToastParams {
  type: TypeOptions;
  message: string;
  config?: ToastOptions;
  fontSize?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
}

// Font size mapping to ensure Tailwind classes are properly generated
const FONT_SIZE_CLASSES: Record<NonNullable<ToastParams['fontSize']>, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
} as const;

// Memoized icon components to prevent recreation on every call
const SuccessIcon = memo(() => <CircleCheckBig className="text-[#0B8A00] flex-shrink-0 w-[20px] h-[20px]" />);
SuccessIcon.displayName = 'SuccessIcon';

const ErrorIcon = memo(() => <CircleX className="text-[#CC0202] flex-shrink-0 w-[20px] h-[20px]" />);
ErrorIcon.displayName = 'ErrorIcon';

// Memoized icon renderer to prevent function recreation
const createIconRenderer = () => {
  return (props: IconProps) => {
    if (props.type === 'success') return <SuccessIcon />;
    if (props.type === 'error') return <ErrorIcon />;
    return null;
  };
};

export function useCustomToast() {
  // Memoize the icon renderer to prevent recreation on every render
  const iconRenderer = useMemo(() => createIconRenderer(), []);

  // Memoize showToast function to prevent recreation and ensure stable reference
  const showToast = useCallback(
    ({ type, message, config, fontSize = 'xs' }: ToastParams) => {
      // Get the correct font size class from the mapping
      const fontSizeClass = FONT_SIZE_CLASSES[fontSize] || FONT_SIZE_CLASSES.xs;

      // Create toast config with memoized icon renderer
      // Using type assertion for toastClassName as it's valid at runtime but not in TS definitions
      const toastConfig = {
        icon: iconRenderer,
        bodyClassName: `font-inter ${fontSizeClass}`,
        toastClassName: 'z-[99999]',
        ...config,
      } as ToastOptions & { toastClassName?: string };

      // Use toast methods based on type
      switch (type) {
        case 'success':
          return toast.success(message, toastConfig);
        case 'error':
          return toast.error(message, toastConfig);
        case 'info':
          return toast.info(message, toastConfig);
        case 'warning':
          return toast.warning(message, toastConfig);
        default:
          return toast(message, toastConfig);
      }
    },
    [iconRenderer]
  );

  return showToast;
}
